import { Tab, Tabs as MuiTabs, styled } from '@mui/material';
import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

interface CustomTabsProps {
  tabs: {
    label: string;
    value: string;
    path?: string;
    disabled?: boolean;
  }[];
  value?: string;
  onChange?: (value: string) => void;
  useRouter?: boolean;
}

const StyledTabs = styled(MuiTabs)({
  minHeight: 'unset',
  '& .MuiTabs-indicator': {
    display: 'none',
  },
});

const StyledTab = styled(Tab)(({ theme }) => ({
  textTransform: 'none',
  minWidth: 'unset',
  minHeight: 'unset',
  padding: '8px 16px',
  borderRadius: '6px',
  fontSize: '16px',
  fontWeight: 400,
  color: '#000000',
  '&:hover': {
    color: '#101828',
  },
  '&.Mui-selected': {
    color: '#702DFF',
    backgroundColor: '#F1F3FF',
    borderRadius: '28px',
  },
  '&.MuiTab-root': {
    marginRight: '8px',
  },
}));

const Tabs: React.FC<CustomTabsProps> = ({
  tabs,
  value: controlledValue,
  onChange: controlledOnChange,
  useRouter = true
}) => {
  const navigate = useNavigate();
  const location = useLocation();

  // Get the current path from the URL
  const currentPath = location.pathname;

  // Find the matching tab value based on the current path
  const getTabValueFromPath = () => {
    const matchingTab = tabs.find(tab => tab.path === currentPath);
    return matchingTab?.value ?? tabs[0]?.value;
  };

  // Use controlled value if provided, otherwise use router-based value
  const value = controlledValue ?? getTabValueFromPath();

  const handleChange = (_: React.SyntheticEvent, newValue: string) => {
    if (useRouter) {
      const selectedTab = tabs.find(tab => String(tab.value) === String(newValue));
      const path = selectedTab?.path;
      if (path != null && path !== '') {
        navigate(path);
      }
    }

    if (controlledOnChange) {
      controlledOnChange(newValue);
    }
  };

  return (
    <StyledTabs
      value={value}
      onChange={handleChange}
      aria-label="custom tabs"
    >
      {tabs.map((tab) => (
        <StyledTab
          key={tab.value}
          label={tab.label}
          value={tab.value}
          disabled={tab.disabled || false}
          disableRipple={tab.disabled || false}
        />
      ))}
    </StyledTabs>
  );
};

export default Tabs; 